import { ReactComponent as ChargingIcon } from 'assets/Icons/SVGs/Equipment/EquipmentFilter/charging.svg';
import { ReactComponent as WorkingIcon } from 'assets/Icons/SVGs/Equipment/EquipmentFilter/working.svg';
import { ReactComponent as DrivingIcon } from 'assets/Icons/SVGs/Equipment/EquipmentFilter/transport.svg';
import { ReactComponent as IdleIcon } from 'assets/Icons/SVGs/Equipment/EquipmentFilter/idle.svg';
import { ReactComponent as OffIcon } from 'assets/Icons/SVGs/Equipment/EquipmentFilter/off.svg';

import { ReactComponent as ScheduledIcon } from 'assets/Icons/SVGs/Job/scheduled.svg';
import { ReactComponent as StartedIcon } from 'assets/Icons/SVGs/Job/started.svg';
import { ReactComponent as PausedIcon } from 'assets/Icons/SVGs/Job/paused.svg';
import { ReactComponent as SkippedIcon } from 'assets/Icons/SVGs/Job/skipped.svg';
import { ReactComponent as CompletedIcon } from 'assets/Icons/SVGs/Job/completed.svg';
import { getHourUtility } from 'helpers/getHourUtility';
import { queryClient } from 'queryClient';
import { EquipmentBreadCrumb } from './constants';

export const TRACKING_CONSTANTS = {
  OPTIONS: {
    CUSTOMER: [
      { label: 'Show', value: 'show' },
      { label: 'Hide', value: 'hide' }
    ],
    EQUIPMENT_STATUS: [
      { label: 'Working', value: 3, icon: <WorkingIcon /> },
      { label: 'Driving/Trailering', value: 4, icon: <DrivingIcon /> },
      { label: 'Idle', value: 1, icon: <IdleIcon /> },
      { label: 'Transport', value: 2, icon: <DrivingIcon /> },
      { label: 'Charging', value: 5, icon: <ChargingIcon /> },
      { label: 'Off', value: 0, icon: <OffIcon /> }
    ],
    JOB_STATUS: [
      { label: 'Scheduled', value: 2, icon: <ScheduledIcon /> },
      { label: 'Started', value: 3, icon: <StartedIcon /> },
      { label: 'Paused', value: 7, icon: <PausedIcon /> },
      { label: 'Skipped', value: 5, icon: <SkippedIcon /> },
      { label: 'Completed', value: 4, icon: <CompletedIcon /> }
    ]
  },
  SHOW_MAP_MARKERS: {
    CUSTOMER: {
      SHOW: 'show',
      HIDE: 'hide'
    }
  },
  PINS: {
    CUSTOMER: 'customer',
    EQUIPMENT: 'equipment',
    MOBILE_USER: 'mobileUser',
    JOB: 'job'
    // TODO: Add others here
  },
  STACKING_ORDER: {
    JOBS: 999,
    EQUIPMENT: 989,
    BREADCRUMBS: 984,
    CUSTOMER: 979,
    MOBILE_USER: 969
  },
  REFETCH_INTERVAL: 300000 // 5-minutes - 3lakh secs
};

export const HandleSyncTracking = () => {
  queryClient.invalidateQueries({ queryKey: ['TRACKING_EQUIPMENTS_LIST'] });
  queryClient.invalidateQueries({ queryKey: ['TRACKING_JOB_LOCATIONS_LIST'] });
  queryClient.invalidateQueries({ queryKey: ['TRACKING_MOBILE_USERS_LIST'] });
  queryClient.invalidateQueries({ queryKey: ['TRACKING_CUSTOMERS_LIST_IMPERATIVE'] });
};

export const isSameDateAndTime = (obj1, obj2) => {
  const date1 = new Date(obj1.date).toDateString();
  const date2 = new Date(obj2.date).toDateString();

  return date1 === date2 && obj1.start === obj2.start && obj1.end === obj2.end;
};

export const isAnyFilterSelected = (filters, todayDate) => {
  return (
    !isSameDateAndTime(todayDate, filters.date) ||
    Boolean(filters.crews?.length) ||
    Boolean(filters.customers?.length) ||
    Boolean(filters.showCustomerPin === TRACKING_CONSTANTS.SHOW_MAP_MARKERS.CUSTOMER.HIDE) ||
    Boolean(filters.equipments?.length) ||
    Boolean(filters.showEquipmentBreadCrumb !== EquipmentBreadCrumb.BREADCRUMS_OFF) ||
    Boolean(filters.equipmentStatus?.length) ||
    Boolean(filters.jobStatus?.length)
  );
};

//util to validate if the start hour is less than end hour since tracking is only for the chosen date
export const isStartHourLessThanEndHour = (start, end) => {
  let [startHr, startPeriod] = start.split(':');
  let [endHr, endPeriod] = end.split(':');
  if (end === '12:AM') return false;
  if (startPeriod === endPeriod)
    return parseInt(startHr) === 12 ? true : parseInt(startHr) <= parseInt(endHr);
  if (startPeriod !== endPeriod) return startPeriod === 'AM';
};

//util to format the chosen date as per the chosen start or end hour passed as value
export const formatDateForSelectedHour = (date, value, isEnd = false) => {
  return isEnd
    ? getHourUtility(date, value).set({ minute: 59 }).utc().format()
    : getHourUtility(date, value).utc().format();
};

export const getMarkerLabel = (label = '', suff = '') => {
  const combinedLabel = String(`${label} ${suff}`).trim();
  return combinedLabel?.length > 20 ? combinedLabel.slice(0, 20) + '...' : combinedLabel;
};

/**
 * @description Tracking Map Marker opacity by default will be 1. But when a marker is selected, all other icon's opacity will be lower.
 * @param {object} params
 * @param {object} params.selectedPin
 * @param {string} params.selectedPin.type
 * @param {string | number} params.selectedPin.id
 * @param {string} params.type
 * @param {string | number} params.id
 * @returns {number} Opacity for the Tracking Map marker
 */
export const getTrackingMapMarkerOpacity = ({ selectedPin, type, id }) => {
  if (selectedPin?.type) {
    if (selectedPin?.type === type && id === selectedPin?.id) return 1;
    else return 0.5;
  }

  return 1;
};

export const BREADCRUMBSOPTIONS = [
  { label: 'Breadcrumbs Off', value: EquipmentBreadCrumb.BREADCRUMS_OFF },
  { label: 'Breadcrumbs Only', value: EquipmentBreadCrumb.BREADCRUM_ONLY },
  { label: 'Breadcrumbs + Lines', value: EquipmentBreadCrumb.BREADCRUMS_AND_LINES }
];

/**
 * Finds the latest event across all equipment based on event_time
 * @param {Object} data - Object with equipment IDs as keys, each containing pathToDraw array
 * @returns {Object|null} - Returns { lat, lng } of latest event or null if no events found
 */
export const getLatestEventCoordinates = data => {
  if (!data || typeof data !== 'object') return null;

  let latestEvent = null;
  let latestEventTime = null;

  Object.values(data).forEach(equipment => {
    if (!equipment || !Array.isArray(equipment.pathToDraw)) return;

    equipment.pathToDraw.forEach(event => {
      if (!event || !event.event_time) return;

      const eventTime = new Date(event.event_time);

      // Check if this event is later than the current latest
      if (!latestEventTime || eventTime > latestEventTime) {
        latestEventTime = eventTime;
        latestEvent = event;
      }
    });
  });

  // Return latest event coordinates if found
  if (latestEvent && latestEvent.latitude != null && latestEvent.longitude != null) {
    return {
      lat: Number(latestEvent.latitude),
      lng: Number(latestEvent.longitude)
    };
  }

  return null;
};
